import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, MapPin, Car, User, CalendarClock } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { fmtDateTime } from "../lib/format";

const LOGO = "https://customer-assets-jt897jd0.emergentagent.net/job_drive-smart-25/artifacts/vqfz3t07_ENFLEET%20by%20POMEN.png";

export default function ApprovalPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const preAction = params.get("tindakan") || "";
  const [data, setData] = useState(null);
  const [invalid, setInvalid] = useState("");
  const [mode, setMode] = useState(preAction === "tolak" ? "tolak" : "lulus");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!token) { setInvalid("Pautan tidak sah — tiada token ditemui."); return; }
    try {
      const r = await api.get(`/approvals/${token}`);
      setData(r.data);
    } catch (err) {
      setInvalid(apiError(err));
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const decide = async (action) => {
    setError("");
    if (action === "tolak" && !remarks.trim()) {
      setMode("tolak");
      setError("Sila nyatakan sebab penolakan.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/approvals/${token}`, { action, remarks: remarks || null });
      setResult(action);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const b = data?.booking;

  return (
    <div className="min-h-screen relative bg-[#10056E] flex flex-col items-center justify-center p-4 sm:p-6">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-white/5 blur-3xl" />
        <div className="absolute -bottom-40 right-1/4 w-[550px] h-[550px] rounded-full bg-[#2a17a8]/40 blur-3xl" />
      </div>
      <div className="relative z-10 w-full max-w-lg fade-up">
        <div className="flex justify-center mb-8">
          <img src={LOGO} alt="ENFLEET by POMEN" className="h-14 w-auto" />
        </div>
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8" data-testid="approval-page">
          {invalid ? (
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-semibold tracking-tight">Kelulusan Tempahan</h2>
              <p className="text-sm bg-red-50 border border-red-200 text-[#DC3545] rounded-lg p-3" data-testid="approval-invalid-msg">{invalid}</p>
              <Button variant="outline" className="w-full" onClick={() => navigate("/login")} data-testid="approval-goto-login-btn">
                Pergi ke Sistem
              </Button>
            </div>
          ) : result ? (
            <div className="space-y-4 text-center py-4" data-testid="approval-result">
              {result === "lulus" ? (
                <CheckCircle2 className="h-14 w-14 text-[#198754] mx-auto" />
              ) : (
                <XCircle className="h-14 w-14 text-[#DC3545] mx-auto" />
              )}
              <h2 className="font-heading text-xl font-semibold tracking-tight">
                {result === "lulus" ? "Tempahan Diluluskan" : "Tempahan Ditolak"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {result === "lulus"
                  ? `Staf ${b?.driver_name} telah dimaklumkan melalui emel & notifikasi.`
                  : `Staf ${b?.driver_name} telah dimaklumkan tentang penolakan ini.`}
              </p>
              <Button variant="outline" className="w-full" onClick={() => navigate("/login")} data-testid="approval-done-login-btn">
                Pergi ke Sistem
              </Button>
            </div>
          ) : !data ? (
            <p className="text-sm text-muted-foreground">Menyemak pautan kelulusan...</p>
          ) : (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-xl font-semibold tracking-tight">Kelulusan Akhir Tempahan</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Hai <b>{data.admin_name}</b>, sila semak dan beri keputusan akhir.
                </p>
              </div>
              <div className="border rounded-xl divide-y text-sm" data-testid="approval-booking-summary">
                <div className="p-3 flex items-start gap-2.5">
                  <User className="h-4 w-4 text-[#10056E] mt-0.5 shrink-0" />
                  <div><p className="font-semibold">{b.driver_name}</p>{b.unit_name && <p className="text-xs text-muted-foreground">{b.unit_name}</p>}</div>
                </div>
                <div className="p-3 flex items-start gap-2.5">
                  <MapPin className="h-4 w-4 text-[#10056E] mt-0.5 shrink-0" />
                  <div><p className="font-semibold">{b.destination}</p><p className="text-xs text-muted-foreground">{b.purpose} · {b.estimated_distance} km · {b.passengers || 1} penumpang</p></div>
                </div>
                <div className="p-3 flex items-start gap-2.5">
                  <CalendarClock className="h-4 w-4 text-[#10056E] mt-0.5 shrink-0" />
                  <p>{fmtDateTime(b.start_datetime)} → {fmtDateTime(b.end_datetime)}</p>
                </div>
                <div className="p-3 flex items-start gap-2.5">
                  <Car className="h-4 w-4 text-[#10056E] mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold">{b.vehicle_reg}{data.vehicle ? ` — ${data.vehicle.make} ${data.vehicle.model}` : ""}</p>
                    <p className="text-xs text-muted-foreground">Ditugaskan oleh {b.assigned_by}{b.remarks ? ` · Catatan: ${b.remarks}` : ""}</p>
                    {b.cross_unit_reason && <p className="text-xs text-amber-700 mt-0.5">Silang bahagian: {b.cross_unit_reason}</p>}
                  </div>
                </div>
              </div>
              {mode === "tolak" && (
                <div className="space-y-1.5">
                  <Label>Sebab Penolakan</Label>
                  <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Nyatakan sebab..." data-testid="approval-remarks-input" />
                </div>
              )}
              {error && <p className="text-sm text-[#DC3545]" data-testid="approval-error">{error}</p>}
              <div className="flex flex-col sm:flex-row gap-2">
                <Button className="flex-1 h-12 text-base bg-[#198754] hover:bg-[#157347]" disabled={busy}
                  onClick={() => decide("lulus")} data-testid="approval-approve-btn">
                  <CheckCircle2 className="h-5 w-5 mr-2" /> Luluskan Tempahan
                </Button>
                <Button variant="outline" className="flex-1 h-12 text-base text-[#DC3545] border-[#DC3545] hover:bg-red-50" disabled={busy}
                  onClick={() => (mode === "tolak" ? decide("tolak") : setMode("tolak"))} data-testid="approval-reject-btn">
                  <XCircle className="h-5 w-5 mr-2" /> {mode === "tolak" ? "Sahkan Penolakan" : "Tolak"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
